import MainLayout from "../../../layouts/MainLayout";
import { Col, Container, Row } from "reactstrap";
import FilePicker from "../../../components/Inputs/FilePicker";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL, STORAGE_URL } from "../../../utils/constants";
import { toast } from "react-toastify";
import { generateKey, tField } from "../../../utils/helpers";
import Select from "react-select";
import { SelectCustomStyles } from "../../../utils/styles";
import { useTranslation } from "react-i18next";
import i18n from 'i18next';
import { IoMdClose } from "react-icons/io";
import {
  IoImagesOutline, IoDocumentTextOutline, IoHomeOutline,
  IoSparklesOutline, IoCashOutline,
  IoLocationOutline, IoCheckmark, IoSearchOutline, IoMapOutline
} from "react-icons/io5";
import {
  IoBedOutline, IoWaterOutline, IoCarOutline,
  IoPeopleOutline, IoFlashOutline, IoWifiOutline
} from "react-icons/io5";
import { MdBalcony } from "react-icons/md";
import { MapContainer, TileLayer, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './create.css';

const NOMINATIM = 'https://nominatim.openstreetmap.org';
const TRIPOLI = [32.9, 13.19];

// Fires reverse geocode on every map drag-end / zoom-end
function MapCenterTracker({ onMoveEnd }) {
  useMapEvents({
    moveend: (e) => {
      const { lat, lng } = e.target.getCenter();
      onMoveEnd(lat, lng);
    }
  });
  return null;
}

// Pans the map when the user picks an autocomplete suggestion
function MapFlyTo({ pos }) {
  const map = useMap();
  useEffect(() => {
    if (pos) map.setView(pos, 15, { animate: true });
  }, [pos, map]);
  return null;
}

const INIT_STATE = (user) => ({
  phone_number: user?.phone_number || '',
  is_premium: false,
  property_for: 'rent',
  category_id: '',
  city_id: '',
  price: '',
  description: '',
  area_size: '',
  floor_number: 0,
  bedrooms_count: 0,
  bathrooms_count: 0,
  is_furnished: false,
  has_parking: false,
  for_families: false,
  electricity_included: false,
  water_included: false,
  internet_included: false,
  map_location: '',
  latitude: '',
  longitude: '',
  license_number: '',
});

function Stepper({ value, onChange, min = 0, max = 30 }) {
  return (
    <div className="cp-stepper">
      <button type="button" className="cp-stepper__btn" onClick={() => onChange(Math.max(min, (value || 0) - 1))}>−</button>
      <span className="cp-stepper__val">{value ?? 0}</span>
      <button type="button" className="cp-stepper__btn" onClick={() => onChange(Math.min(max, (value || 0) + 1))}>+</button>
    </div>
  );
}

function FeatureChip({ active, onClick, icon, label }) {
  return (
    <button type="button" className={'cp-feature-chip' + (active ? ' active' : '')} onClick={onClick}>
      {icon}{label}
    </button>
  );
}

function CreatePropertyPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user'));
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [storeLoading, setStoreLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadLength, setUploadLength] = useState(0);
  const [imagesThumbnails, setImagesThumbnails] = useState([]);
  const [filePickerResetKey, setFilePickerResetKey] = useState(0);

  // Location picker state
  const [mapOpen, setMapOpen] = useState(false);
  const [mapFlyTo, setMapFlyTo] = useState(null);       // pans map to this pos
  const [mapCurrentCenter, setMapCurrentCenter] = useState(null); // lat/lng under pin
  const [mapPinAddress, setMapPinAddress] = useState('');
  const [reverseLoading, setReverseLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef(null);
  const suggestionsRef = useRef(null);

  const isEditMode = !!location.state?.property;
  const [editableProperty, setEditableProperty] = useState(location.state?.property || null);
  const [state, setState] = useState(INIT_STATE(user));
  const tempIdRef = useRef(null);

  const set = (key, value) => setState(prev => ({ ...prev, [key]: value }));
  const toggle = (key) => setState(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (isEditMode && editableProperty) {
      setState({
        phone_number:         editableProperty.phone_number || '',
        is_premium:           !!editableProperty.is_premium,
        property_for:         editableProperty.property_for || 'rent',
        category_id:          editableProperty.category_id || '',
        city_id:              editableProperty.city_id || '',
        price:                editableProperty.price || '',
        description:          editableProperty.description_ar || '',
        area_size:            editableProperty.area_size || '',
        floor_number:         editableProperty.floor_number || 0,
        bedrooms_count:       editableProperty.bedrooms_count || 0,
        bathrooms_count:      editableProperty.bathrooms_count || 0,
        is_furnished:         !!editableProperty.is_furnished,
        has_parking:          !!editableProperty.has_parking,
        for_families:         !!editableProperty.for_families,
        electricity_included: !!editableProperty.electricity_included,
        water_included:       !!editableProperty.water_included,
        internet_included:    !!editableProperty.internet_included,
        map_location:         editableProperty.map_location || editableProperty.address || '',
        latitude:             editableProperty.latitude || '',
        longitude:            editableProperty.longitude || '',
        license_number:       editableProperty.license_number || '',
      });
      if (editableProperty.latitude && editableProperty.longitude) {
        const pos = [parseFloat(editableProperty.latitude), parseFloat(editableProperty.longitude)];
        setMapFlyTo(pos);
        setMapCurrentCenter(pos);
        setMapPinAddress(editableProperty.map_location || editableProperty.address || '');
      }
    }
    getCategories();
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setSuggestionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getCategories = () => {
    fetch(API_URL + '/categories', { headers: { 'Content-Type': 'application/json' } })
      .then(r => r.json())
      .then(data => { if (data.success) { setCategories(data.categories); getStates(); } });
  };

  const getStates = () => {
    fetch(API_URL + '/states', { headers: { 'Content-Type': 'application/json' } })
      .then(r => r.json())
      .then(data => { if (data.success) setStates(data.states); });
  };

  // ── Nominatim address autocomplete ──
  const handleLocationInput = (value) => {
    set('map_location', value);
    set('latitude', '');
    set('longitude', '');
    clearTimeout(searchTimerRef.current);
    if (!value || value.length < 3) { setSuggestions([]); setSuggestionsOpen(false); return; }
    searchTimerRef.current = setTimeout(() => {
      setSearchLoading(true);
      fetch(`${NOMINATIM}/search?q=${encodeURIComponent(value)}&format=json&countrycodes=ly&limit=6&accept-language=ar`, {
        headers: { 'Accept-Language': 'ar' }
      })
      .then(r => r.json())
      .then(data => {
        setSuggestions(data || []);
        setSuggestionsOpen((data || []).length > 0);
        setSearchLoading(false);
      })
      .catch(() => setSearchLoading(false));
    }, 350);
  };

  const selectSuggestion = (item) => {
    const pos = [parseFloat(item.lat), parseFloat(item.lon)];
    setState(prev => ({ ...prev, map_location: item.display_name, latitude: item.lat, longitude: item.lon }));
    setMapFlyTo(pos);
    setMapCurrentCenter(pos);
    setMapPinAddress(item.display_name);
    setSuggestionsOpen(false);
    setSuggestions([]);
  };

  // ── Fires when the map stops moving — reverse geocode the new center ──
  const handleMoveEnd = async (lat, lng) => {
    setMapCurrentCenter([lat, lng]);
    setMapPinAddress('');
    setReverseLoading(true);
    try {
      const r = await fetch(`${NOMINATIM}/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=ar`, {
        headers: { 'Accept-Language': 'ar' }
      });
      const data = await r.json();
      setMapPinAddress(data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    } catch {
      setMapPinAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    } finally {
      setReverseLoading(false);
    }
  };

  const confirmMapLocation = () => {
    if (!mapCurrentCenter) return;
    setState(prev => ({
      ...prev,
      map_location: mapPinAddress,
      latitude: String(mapCurrentCenter[0]),
      longitude: String(mapCurrentCenter[1]),
    }));
    setMapOpen(false);
  };

  const openMapPicker = () => {
    setMapOpen(true);
    if (state.latitude && state.longitude) {
      // Already has saved coords — fly back to them
      const pos = [parseFloat(state.latitude), parseFloat(state.longitude)];
      setMapFlyTo(pos);
      setMapCurrentCenter(pos);
    } else {
      // Set Tripoli as default so confirm is never permanently disabled
      setMapCurrentCenter(TRIPOLI);
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            const pos = [coords.latitude, coords.longitude];
            setMapFlyTo(pos);
            setMapCurrentCenter(pos);
          },
          () => {} // denied/unavailable — stays on Tripoli
        );
      }
    }
  };

  // ── File upload ──
  const uploadFiles = useCallback(async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploadLoading(true);
    setUploadLength(fileList.length);
    const form = new FormData();
    for (const f of fileList) form.append('files[]', f);
    if (tempIdRef.current) form.append('temp_id', tempIdRef.current);
    if (isEditMode && editableProperty) form.append('property_id', editableProperty.id);
    try {
      const res = await fetch(API_URL + '/user/properties/files/upload', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') },
        body: form
      });
      const json = await res.json();
      if (!json?.success) { toast.error(json?.message || t('create_property.errors.upload_failed')); return; }
      tempIdRef.current = json.temp_id || tempIdRef.current;
      const newFiles = (json.files || []).map(f => ({ id: f.image_id, path: f.image_path }));
      setImagesThumbnails(prev => [...prev, ...newFiles]);
      setFilePickerResetKey(prev => prev + 1);
    } catch {
      toast.error(t('create_property.errors.upload_error'));
    } finally {
      setUploadLoading(false);
    }
  }, []);

  const removeFile = async (imageId, imagePath) => {
    setImagesThumbnails(prev => prev.filter(img => img.id !== imageId));
    if (editableProperty) {
      setEditableProperty(prev => ({
        ...prev,
        images: prev.images?.filter(img => img.id !== imageId) || [],
        videos: prev.videos?.filter(vid => vid.id !== imageId) || [],
      }));
    }
    try {
      await fetch(API_URL + '/user/properties/files/destroy', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') },
        body: JSON.stringify({ id: imageId, path: imagePath })
      });
    } catch {}
  };

  const handleImagesChange = useCallback((files) => uploadFiles(Array.from(files || [])), [uploadFiles]);

  const store = () => {
    setStoreLoading(true);
    const formData = new FormData();
    formData.append('temp_id', tempIdRef.current || '');
    formData.append('phone_number', state.phone_number);
    formData.append('is_premium', state.is_premium ? 1 : 0);
    formData.append('property_for', state.property_for);
    formData.append('category_id', state.category_id);
    formData.append('city_id', state.city_id);
    formData.append('price', Number(state.price) || '');
    formData.append('description', state.description);
    formData.append('area_size', state.area_size || '');
    formData.append('floor_number', state.floor_number || 0);
    formData.append('bedrooms_count', state.bedrooms_count || 0);
    formData.append('bathrooms_count', state.bathrooms_count || 0);
    formData.append('is_furnished', state.is_furnished ? 1 : 0);
    formData.append('has_parking', state.has_parking ? 1 : 0);
    formData.append('for_families', state.for_families ? 1 : 0);
    formData.append('electricity_included', state.electricity_included ? 1 : 0);
    formData.append('water_included', state.water_included ? 1 : 0);
    formData.append('internet_included', state.internet_included ? 1 : 0);
    formData.append('map_location', state.map_location);
    formData.append('address', state.map_location);
    formData.append('latitude', state.latitude || '');
    formData.append('longitude', state.longitude || '');
    formData.append('license_number', state.license_number);

    const URL = isEditMode && editableProperty
      ? API_URL + '/user/properties/update/' + editableProperty.id
      : API_URL + '/user/properties/store';

    fetch(URL, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') },
      body: formData
    })
    .then(r => r.json())
    .then(data => {
      if (data.success) {
        navigate('/my_properties?p=' + generateKey());
      } else {
        toast.error(data.message || t('create_property.errors.store_error'));
      }
      setStoreLoading(false);
    })
    .catch(() => { setStoreLoading(false); });
  };

  const cityOptions = states.map(s => ({
    label: tField(s, 'name'),
    options: s.cities.map(c => ({ value: c.id, label: tField(c, 'name') })),
  }));
  const selectedCity = states.flatMap(s => s.cities).find(c => c.id === state.city_id);
  const selectedCityOption = selectedCity ? { value: state.city_id, label: tField(selectedCity, 'name') } : null;

  const categoryOptions = categories.map(c => ({ value: c.id, label: tField(c, 'name') }));
  const selectedCategoryOption = state.category_id
    ? { value: state.category_id, label: categories.find(c => c.id === state.category_id)?.['name_' + i18n.language] }
    : null;

  const canSubmit = !uploadLoading && !storeLoading && state.category_id && state.city_id && state.property_for;

  const features = [
    { key: 'is_furnished',         label: t('create_property.features.is_furnished'),         icon: <MdBalcony size={16} /> },
    { key: 'has_parking',          label: t('create_property.features.has_parking'),           icon: <IoCarOutline size={16} /> },
    { key: 'for_families',         label: t('create_property.features.for_families'),          icon: <IoPeopleOutline size={16} /> },
    { key: 'electricity_included', label: t('create_property.features.electricity_included'),  icon: <IoFlashOutline size={16} /> },
    { key: 'water_included',       label: t('create_property.features.water_included'),        icon: <IoWaterOutline size={16} /> },
    { key: 'internet_included',    label: t('create_property.features.internet_included'),     icon: <IoWifiOutline size={16} /> },
  ];

  const mapInitialCenter = mapCurrentCenter || TRIPOLI;

  return (
    <MainLayout isTopBar screenTitle={isEditMode ? t('create_property.titles.edit') : t('create_property.titles.create')}>
      <Container>
        <Row className="justify-content-center mt-3 mb-4">
          <Col md="8" lg="6">

            {/* ── SECTION: Media ── */}
            <div className="cp-section">
              <div className="cp-section__header">
                <div className="cp-section__icon"><IoImagesOutline size={19} /></div>
                <h2 className="cp-section__title">{t('create_property.fields.images.label')}</h2>
              </div>
              <FilePicker
                onChange={handleImagesChange}
                images={imagesThumbnails}
                onRemove={removeFile}
                uploadLoading={uploadLoading}
                uploadLength={uploadLength}
                resetKey={filePickerResetKey}
              />
              {isEditMode && editableProperty && (
                <div className="cp-thumbs">
                  {editableProperty?.images?.map(img => (
                    <div className="cp-thumb" key={img.id}>
                      <img src={STORAGE_URL + img.image_path} alt="" />
                      <button className="cp-thumb__remove" onClick={() => removeFile(img.id, img.image_path)}>
                        <IoMdClose size={13} />
                      </button>
                    </div>
                  ))}
                  {editableProperty?.videos?.map(vid => (
                    <div className="cp-thumb" key={vid.id}>
                      <img src={STORAGE_URL + vid.thumbnail_path} alt="" />
                      <button className="cp-thumb__remove" onClick={() => removeFile(vid.id, vid.thumbnail_path)}>
                        <IoMdClose size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── SECTION: Basic Info ── */}
            <div className="cp-section">
              <div className="cp-section__header">
                <div className="cp-section__icon"><IoDocumentTextOutline size={19} /></div>
                <h2 className="cp-section__title">{t('create_property.sections.basic_info')}</h2>
              </div>

              <div className="mb-3">
                <label className="cp-label">{t('create_property.fields.purpose.label')}</label>
                <div className="cp-purpose-row">
                  {[
                    { v: 'rent',     l: t('create_property.fields.purpose.options.rent') },
                    { v: 'sale',     l: t('create_property.fields.purpose.options.sale') },
                    { v: 'exchange', l: t('create_property.fields.purpose.options.exchange') },
                  ].map(o => (
                    <button
                      key={o.v}
                      type="button"
                      className={'cp-purpose-chip' + (state.property_for === o.v ? ' active' : '')}
                      onClick={() => set('property_for', o.v)}
                    >{o.l}</button>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <label className="cp-label">{t('create_property.fields.category.label')}</label>
                <Select
                  placeholder={t('create_property.fields.category.placeholder')}
                  options={categoryOptions}
                  value={selectedCategoryOption}
                  onChange={opt => set('category_id', opt.value)}
                  styles={SelectCustomStyles}
                  isSearchable={false}
                />
              </div>

              <div className="mb-0">
                <label className="cp-label">{t('create_property.fields.city.label')}</label>
                <Select
                  placeholder={t('create_property.fields.city.placeholder')}
                  options={cityOptions}
                  value={selectedCityOption}
                  onChange={opt => set('city_id', opt.value)}
                  styles={SelectCustomStyles}
                />
              </div>
            </div>

            {/* ── SECTION: Property Details ── */}
            <div className="cp-section">
              <div className="cp-section__header">
                <div className="cp-section__icon"><IoHomeOutline size={19} /></div>
                <h2 className="cp-section__title">{t('create_property.sections.property_details')}</h2>
              </div>

              <div className="mb-3">
                <label className="cp-label">{t('create_property.fields.area_size.label')}</label>
                <div className="cp-input-wrap">
                  <input
                    className="form-control"
                    type="number"
                    inputMode="numeric"
                    placeholder={t('create_property.fields.area_size.placeholder')}
                    value={state.area_size}
                    onChange={e => set('area_size', e.target.value)}
                  />
                  <span className="cp-input-suffix">{t('create_property.fields.area_size.suffix')}</span>
                </div>
              </div>

              <div className="cp-stepper-grid">
                <div className="cp-stepper-item">
                  <label className="cp-label" style={{ marginBottom: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <IoBedOutline size={16} /> {t('create_property.fields.bedrooms_count.label')}
                    </span>
                  </label>
                  <Stepper value={state.bedrooms_count} onChange={v => set('bedrooms_count', v)} />
                </div>
                <div className="cp-stepper-item">
                  <label className="cp-label" style={{ marginBottom: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <IoWaterOutline size={16} /> {t('create_property.fields.bathrooms_count.label')}
                    </span>
                  </label>
                  <Stepper value={state.bathrooms_count} onChange={v => set('bathrooms_count', v)} />
                </div>
                <div className="cp-stepper-item">
                  <label className="cp-label" style={{ marginBottom: 0 }}>{t('create_property.fields.floor_number.label')}</label>
                  <Stepper value={state.floor_number} onChange={v => set('floor_number', v)} max={100} />
                </div>
              </div>
            </div>

            {/* ── SECTION: Features ── */}
            <div className="cp-section">
              <div className="cp-section__header">
                <div className="cp-section__icon"><IoSparklesOutline size={19} /></div>
                <h2 className="cp-section__title">{t('create_property.sections.features')}</h2>
              </div>
              <div className="cp-features-grid">
                {features.map(f => (
                  <FeatureChip
                    key={f.key}
                    active={!!state[f.key]}
                    onClick={() => toggle(f.key)}
                    icon={f.icon}
                    label={f.label}
                  />
                ))}
              </div>
            </div>

            {/* ── SECTION: Price & Contact ── */}
            <div className="cp-section">
              <div className="cp-section__header">
                <div className="cp-section__icon"><IoCashOutline size={19} /></div>
                <h2 className="cp-section__title">{t('create_property.sections.price_contact')}</h2>
              </div>

              <div className="mb-3">
                <label className="cp-label">{t('create_property.fields.price.label')}</label>
                <div className="cp-input-wrap">
                  <input
                    className="form-control"
                    type="number"
                    inputMode="numeric"
                    placeholder="0"
                    value={state.price}
                    onChange={e => set('price', e.target.value)}
                  />
                  <span className="cp-input-suffix">د.ل</span>
                </div>
              </div>

              <div>
                <label className="cp-label">{t('create_property.fields.phone_number.label')}</label>
                <input
                  className="form-control"
                  type="tel"
                  inputMode="tel"
                  placeholder={t('create_property.fields.phone_number.placeholder')}
                  value={state.phone_number}
                  onChange={e => set('phone_number', e.target.value)}
                />
                <small className="text-muted" style={{ fontSize: '0.78rem' }}>
                  {t('create_property.fields.phone_number.help_text')}
                </small>
              </div>
            </div>

            {/* ── SECTION: Location ── */}
            <div className="cp-section">
              <div className="cp-section__header">
                <div className="cp-section__icon"><IoLocationOutline size={19} /></div>
                <h2 className="cp-section__title">{t('create_property.sections.location')}</h2>
              </div>

              {/* Autocomplete + map button */}
              <div className="mb-3" ref={suggestionsRef} style={{ position: 'relative' }}>
                <label className="cp-label">{t('create_property.fields.location_search.label')}</label>
                <div className="cp-location-row">
                  <div className="cp-location-input-wrap">
                    <IoSearchOutline className="cp-location-icon" size={17} />
                    <input
                      className="form-control cp-location-input"
                      placeholder={t('create_property.fields.location_search.placeholder')}
                      value={state.map_location}
                      onChange={e => handleLocationInput(e.target.value)}
                      onFocus={() => suggestions.length > 0 && setSuggestionsOpen(true)}
                      autoComplete="off"
                    />
                    {searchLoading && (
                      <span className="cp-location-spinner spinner-border spinner-border-sm" />
                    )}
                    {state.latitude && (
                      <span className="cp-location-check"><IoCheckmark size={15} /></span>
                    )}
                  </div>
                  <button type="button" className="cp-map-btn" onClick={openMapPicker}>
                    <IoMapOutline size={18} />
                    <span>{t('create_property.fields.location_search.map_button')}</span>
                  </button>
                </div>

                {/* Autocomplete suggestions */}
                {suggestionsOpen && suggestions.length > 0 && (
                  <div className="cp-suggestions">
                    {suggestions.map((item, i) => (
                      <button
                        key={i}
                        type="button"
                        className="cp-suggestion-item"
                        onClick={() => selectSuggestion(item)}
                      >
                        <IoLocationOutline size={16} className="cp-suggestion-icon" />
                        <span>{item.display_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Coordinates badge — shown after location is set */}
              {state.latitude && state.longitude && (
                <div className="cp-coords-badge">
                  <IoLocationOutline size={15} />
                  <span dir="ltr">{parseFloat(state.latitude).toFixed(5)}, {parseFloat(state.longitude).toFixed(5)}</span>
                </div>
              )}

              {/* License number */}
              <div className="mt-3">
                <label className="cp-label">{t('create_property.fields.license_number.label')} <span className="text-muted fw-normal">{t('create_property.fields.license_number.optional')}</span></label>
                <input
                  className="form-control"
                  placeholder={t('create_property.fields.license_number.placeholder')}
                  value={state.license_number}
                  onChange={e => set('license_number', e.target.value)}
                />
              </div>
            </div>

            {/* ── SECTION: Description ── */}
            <div className="cp-section">
              <div className="cp-section__header">
                <div className="cp-section__icon"><IoDocumentTextOutline size={19} /></div>
                <h2 className="cp-section__title">{t('create_property.fields.description.label')}</h2>
              </div>
              <textarea
                className="form-control"
                rows={5}
                placeholder={t('create_property.fields.description.placeholder')}
                value={state.description}
                onChange={e => set('description', e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>

            {/* ── Premium toggle (create only) ── */}
            {!isEditMode && (
              <div
                className={'cp-premium-toggle' + (state.is_premium ? ' active' : '')}
                onClick={() => toggle('is_premium')}
                style={{ marginBottom: 14 }}
              >
                <span className="cp-premium-toggle__icon">⭐</span>
                <div className="cp-premium-toggle__text">
                  <p className="cp-premium-toggle__title">{t('create_property.fields.is_premium.label')}</p>
                  <p className="cp-premium-toggle__sub">{t('create_property.fields.is_premium.subtitle')}</p>
                </div>
                <div className="cp-premium-toggle__check">
                  {state.is_premium && <IoCheckmark size={14} color="#fff" />}
                </div>
              </div>
            )}

          </Col>
        </Row>
      </Container>

      {/* ── Fixed submit bar ── */}
      <div className="cp-submit-bar">
        <button className="cp-submit-btn" onClick={store} disabled={!canSubmit}>
          {storeLoading ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <>
              {isEditMode ? t('create_property.buttons.edit') : t('create_property.buttons.create')}
            </>
          )}
        </button>
      </div>

      {/* ── MAP PICKER MODAL ── */}
      {mapOpen && (
        <div className="cp-map-modal">
          <div className="cp-map-modal__header">
            <span className="cp-map-modal__title">{t('create_property.map.title')}</span>
            <button className="cp-map-modal__close-btn" onClick={() => setMapOpen(false)}>
              <IoMdClose size={20} />
            </button>
          </div>

          <div className="cp-map-modal__hint">
            {t('create_property.map.hint')}
          </div>

          <div className="cp-map-modal__map">
            <MapContainer
              center={mapInitialCenter}
              zoom={mapCurrentCenter ? 15 : 12}
              style={{ width: '100%', height: '100%' }}
              zoomControl={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <MapCenterTracker onMoveEnd={handleMoveEnd} />
              {mapFlyTo && <MapFlyTo pos={mapFlyTo} />}
            </MapContainer>

            {/* Fixed center pin overlay */}
            <div className="cp-map-center-pin" aria-hidden="true">
              <div className="cp-map-center-pin__dot" />
              <div className="cp-map-center-pin__tail" />
              <div className="cp-map-center-pin__shadow" />
            </div>
          </div>

          <div className="cp-map-modal__bar">
            <div className="cp-map-modal__address">
              {reverseLoading ? (
                <span className="text-muted" style={{ fontSize: '0.83rem' }}>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {t('create_property.map.loading_address')}
                </span>
              ) : mapPinAddress ? (
                <span style={{ fontSize: '0.83rem' }}>{mapPinAddress}</span>
              ) : (
                <span style={{ fontSize: '0.83rem', color: '#9ca3af' }}>
                  {t('create_property.map.tap_to_set')}
                </span>
              )}
            </div>
            <button
              className="cp-map-modal__confirm"
              onClick={confirmMapLocation}
              disabled={!mapCurrentCenter || reverseLoading}
            >
              {t('create_property.map.confirm')}
            </button>
          </div>
        </div>
      )}
    </MainLayout>
  );
}

export default CreatePropertyPage;
